import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { AppBuilderAPI, ProjectsAPI } from '@makyoapp/makyo-sdk'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { randomAppColor, randomAppIcon } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useGetBrandKits } from '@/resolvers/org/brand-kit/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { PROJECT_ROUTE } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'

/**
 * useOrgAppAction is a hook that handles the app related actions.
 *
 * @returns {Object} - An object containing the following properties:
 * - canAddNewApp: A boolean indicating if the current user has permission to add a new app.
 * - handleAddNewApp: A callback function that creates a new app.
 * - handleDeleteApp: A callback function that deletes an app.
 */
export const useOrgAppAction = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const { userData } = useAuth()
  const userId = userData?.id

  const { orgsPerms, recentApps, updateRecentApps } = useOrgsContext()
  const { currentOrg } = useCurrentOrgContext()

  const brandkitsQuery = useGetBrandKits()

  const canAddNewApp = !!currentOrg && orgsPerms[currentOrg.id]?.[OrgPermission.AppsCreateNewApp]

  const printAPIError = useReqError()

  const revalidateApps = useCallback(() => {
    if (!!currentOrg) {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.orgApps, currentOrg.id],
      })
    }
    // validate trash list
  }, [currentOrg, queryClient])

  /**
   * handleDelete is a callback function that deletes a project.
   */
  const handleDeleteApp = useCallback(
    async (id: string, closeModal: () => void) => {
      await ProjectsAPI.ReqDelete(id)
        .then(async () => {
          queryClient.invalidateQueries({ queryKey: [queryKeys.user.trash] })

          // on delete success we do:
          // - update list of apps
          // - update recent apps for toolbar if needed
          await revalidateApps()

          toasted(t('App moved to trash'))

          const inRecent = recentApps.find((app) => app.id === id)
          if (inRecent) {
            try {
              if (userId) {
                const key = `recentApps:${userId}`
                const raw = localStorage.getItem(key)
                const parsed = raw ? JSON.parse(raw) : []
                const list: any[] = Array.isArray(parsed) ? parsed : []
                const filtered = list.filter((a) => a && a.id !== id)
                localStorage.setItem(key, JSON.stringify(filtered))
                window.dispatchEvent(new CustomEvent(EVENT_KEYS.RECENT_APPS_UPDATED, { detail: filtered }))
              }
            } catch (_) {
              // ignore localStorage errors
            }
            updateRecentApps()
          }
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => closeModal())
    },
    [revalidateApps, recentApps, t, updateRecentApps, printAPIError, queryClient]
  )

  /**
   * handleAdd is a callback function that creates a new project.
   *
   * @todo:
   * - generate loginform after the app is created
   */
  const handleAddNewApp = useCallback(
    async (newProjectName: string, team: string, dbId: string, closeModal: () => void) => {
      // Ensure the current organization exists before attempting to create a project
      if (currentOrg) {
        // Make a request to create the project
        await ProjectsAPI.ReqAdd({
          _Name: newProjectName,
          _Icon: randomAppIcon(newProjectName),
          _IconBgColor: randomAppColor(newProjectName),
          _TeamID: team,
          _DatabaseID: dbId,
        })
          .then(async (res) => {
            revalidateApps()

            const newAppId = res._id

            // todo: create a default brandkit if there is no brandkit in current org

            // add default brandkit to the app
            const defaultBrandkit = brandkitsQuery.data?.[0]
            if (defaultBrandkit) {
              await AppBuilderAPI.ReqUpdateStyle(newAppId, { _BrandKitID: defaultBrandkit.id, _ColorSchemeID: '0' })
            }

            // set a flag to handle new app creation in AppBuilderContext
            window.sessionStorage.setItem('newAppId', newAppId)

            // navigate to the new app's page
            navigate(`${PROJECT_ROUTE}/${newAppId}`)

            // display a success message
            toasted(t('App Created'))
          })
          .catch((e) => {
            // Display the error message
            printAPIError(e)
          })
          .finally(() => {
            closeModal()
          })
      }
    },
    // Dependencies for the callback
    [currentOrg, revalidateApps, brandkitsQuery.data, navigate, t, printAPIError]
  )

  return {
    canAddNewApp,
    handleAddNewApp,
    handleDeleteApp,
  }
}
